import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    FormControl,
    FormHelperText,
    Grid,
    Paper,
    TextField,
    Typography } from "../../ui";
import SnpButton from "../../global/SnpButton";
import ThreeDText from "../../global/ThreeDText";
import {
  CART_INITIAL_STATE,
  GET_CUSTOMER_DETAIS,
  isEmpty,
  printDebugLog,
  SUCCESS
} from "../../../constants/constant";
import { snpAxios } from "../../global/api";
import Loader from "../../global/loader/Loader";
import "../FlipCard.css";
import uiUtilInstance from "../../../utilities/uiUtil";

const RenderCustomerInfo = memo(({ baseUrl, shopHash, customerData, setCustomerData, isPaid, destinationAddress, updateColumnBasedOnAssociateBenefits }) => {

    const INITIAL_CUSTOMER_DATA = {
        custId: "",
        custMob: "",
        associateId: "",
        custName: "",
        custEmail: "",
        address: "",
        custGstDetails: "",
        custCompanyName: "",
        assosiateAvailable: false,
        useAssociateBenefit: true,
        customerChangingProcess: false,
    };
    //const [fieldVisible, setFieldVisible] = useState(false);
    const [customerFieldsVisible, setCustomerFieldsVisible] = useState(false); // Tracks visibility
    const [animationClass, setAnimationClass] = useState("hidden"); // Tracks animation type
    const [loader, setLoader] = useState(false);
    const mobileRef = useRef(null);

    const deliveryDetails = useMemo(() => {
        const details = { name: "", address: "", mobileNum: "" };
        const rawAddress = String(destinationAddress || "").trim();
        if (isEmpty(rawAddress))
            return details;

        rawAddress.split("\n").forEach(line => {
            const separatorIndex = line.indexOf(":");
            if (separatorIndex < 0) {
                details.address = details.address ? `${details.address}\n${line}` : line;
                return;
            }
            const label = line.slice(0, separatorIndex).trim().toLowerCase();
            const value = line.slice(separatorIndex + 1).trim();
            if (label === "name")
                details.name = value;
            else if (label === "mobile")
                details.mobileNum = value;
            else if (label === "address")
                details.address = value;
            else
                details.address = details.address ? `${details.address}\n${line}` : line;
        });

        if (isEmpty(details.address))
            details.address = rawAddress;
        return details;
    }, [destinationAddress]);


    useEffect(() => {
        printDebugLog("RenderCustomerInfo", customerData);

        if (!isEmpty(customerData.custId)) {
            // If custId is present, show from right to left & disable fields
            setAnimationClass("slide-in-right");
            setCustomerFieldsVisible(true);
            //setFieldVisible(true);
        } else {
            // If custId is empty, show from left to right
            setAnimationClass("slide-in-left");
            if (!customerData.customerChangingProcess) {
                setCustomerFieldsVisible(false);
                //setFieldVisible(false);
            }
        }
        const handleKeyDown = (event) => {
            //printDebugLog("handleKeyDown",event);
            if (event.ctrlKey && event.key === '3') {
                event.preventDefault();
                mobileRef.current?.focus();
            } else if (customerData.assosiateAvailable && event.ctrlKey && event.key === '4') {
                event.preventDefault();
                handleAssociateClicked();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [customerData]);


    const getCustomerData = async (e) => {
        const { name, value } = e.target;
        setCustomerData((prev) => ({ ...prev, [name]: value }));

        if (name === "custMob" && value.length === 10) {
            setLoader(true);
            try {
                const data = {
                    jcJson: {
                        apiToken: CART_INITIAL_STATE.apiToken,
                        shopHash: shopHash,
                        custMob: value
                    }
                };
                const response = await snpAxios.post(`${baseUrl}${GET_CUSTOMER_DETAIS}`, data);
                if (response.data.status !== SUCCESS) {
                    setCustomerData(INITIAL_CUSTOMER_DATA);
                }
                const jcJson = response.data.jcJson;
                if (jcJson.assosiateAvailable) {
                    updateColumnBasedOnAssociateBenefits(true);
                } else {
                    if (customerData.useAssociateBenefit) {
                        updateColumnBasedOnAssociateBenefits(false);
                    }
                }
                setCustomerData(prev => ({
                    ...prev, ...jcJson,
                    useAssociateBenefit: jcJson.assosiateAvailable,
                    customerChangingProcess: false,
                }));

                setCustomerFieldsVisible(true);
                setLoader(false);
            } catch (error) {
                console.error("Error fetching customer data:", error);
                setLoader(false);
            }
        }
    };

    const handleAssociateClicked = useCallback(() => {
        const useAssociateBenefit = !customerData.useAssociateBenefit;
        setCustomerData((prev) => ({
            ...prev,
            useAssociateBenefit: useAssociateBenefit,
        }));
        updateColumnBasedOnAssociateBenefits(useAssociateBenefit);
    }, [customerData]);

    const customerFields = useMemo(
        () => [
            // { label: "Associate Id", name: "associateId", required: true },
            { label: "Customer Name", name: "custName" },
            { label: "Customer Email Address", name: "custEmail" },
            { label: "Customer Address", name: "address" },
            { label: "Customer GST Details", name: "custGstDetails" },
            { label: "Customer Company Details", name: "custCompanyName" },
        ],
        []
    );

    return (
        <Paper
            elevation={0}
            sx={{
                display: "flex",
                flexDirection: "row",
                backgroundColor: "#f2e0cc",
                boxShadow: 1,
                borderRadius: 5,
                padding: 2,
            }}
        >
            {
                loader &&
                <center>
                    <Loader />
                </center>
            }
            <Grid container spacing={1}>

                <Grid item xs={12} sx={{
                    margin: { xs: '16px 0', md: 'none' }
                }}>
                    <ThreeDText text="Customer Information" className="three-d-text-container"
                        justifyContent="left"
                        alignItems="left" />
                </Grid>
                <Typography
                    variant="p" component="div"
                    className="existing-customer-search" sx={{ ml: 1 }}>{uiUtilInstance.showHotKeySpan("ctrl+3", "12px")}</Typography>
                <Grid item xs={12}>
                    <TextField
                        id={"custMob"}
                        label={"Customer Mobile Number"}
                        variant="filled"
                        size="small"
                        inputRef={mobileRef}
                        required={true}
                        name={"custMob"}
                        disabled={isPaid}
                        value={customerData.custMob}
                        onChange={getCustomerData}
                        sx={{ width: "100%" }}
                    />
                </Grid>
                {/* Customer Fields - Hidden until API call */}
                {customerFieldsVisible && (
                    <Grid item xs={12} className={animationClass}>
                        {customerFields.map(({ label, name, required }) => (
                            <Grid item xs={12} key={name}>
                                <TextField
                                    id={name}
                                    label={label}
                                    variant="filled"
                                    size="small"
                                    required={required}
                                    name={name}
                                    disabled={isPaid} // Disable if custId exists
                                    value={customerData[name] || ""}
                                    onChange={(e) => setCustomerData(prev => ({
                                        ...prev,
                                        [name]: e.target.value,
                                        customerChangingProcess: true,
                                    }))}
                                    sx={{ width: "100%" }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
                {
                    !isEmpty(destinationAddress) && (
                        <Grid item xs={12}>
                            <Typography component="div" sx={{ color: '#3e0109' }}>
                                <Typography
                                    component="span"
                                    sx={{ fontWeight: 700, color: '#3e0109' }}
                                >
                                    Delivery Details:
                                </Typography>

                                <Typography
                                    component="span"
                                    sx={{
                                        display: 'block',
                                        mt: 0.5,
                                        color: '#111827',
                                        whiteSpace: 'pre-wrap',
                                        overflowWrap: 'break-word',
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {deliveryDetails.name && `Name: ${deliveryDetails.name}\n`}
                                    {`Address: ${deliveryDetails.address}`}
                                    {deliveryDetails.mobileNum && `\nMobile: ${deliveryDetails.mobileNum}`}
                                </Typography>
                            </Typography>
                        </Grid>
                    )
                }

                {/* Associate Benefits Button */}
                {customerData.assosiateAvailable && (
                    <Grid item xs={6}>
                        <FormControl>
                            <SnpButton
                                variant="contained"
                                mbgcolor="#ff8a5c"
                                aria-describedby="new-customer-button-helper-text"
                                onPointerDown={() => handleAssociateClicked()}
                            >
                                {customerData.useAssociateBenefit ? "Use Normal Benefits" : "Use Associate Benefits"}
                            </SnpButton>
                            <FormHelperText sx={{ textAlign: 'center' }} id="new-customer-button-helper-text">
                                {uiUtilInstance.showHotKeySpan("ctrl+4")}
                            </FormHelperText>
                        </FormControl>
                    </Grid>

                )}
            </Grid>
        </Paper>
    );
});

export default RenderCustomerInfo;
